import ReactEcharts from "echarts-for-react";
import { truncate } from "lodash";
import millify from "millify";
import moment from "moment";
import toast from "react-hot-toast";
import React, { useEffect, useRef, useState } from "react";
import "react-responsive-pagination/themes/classic.css";

import Button from "components/common/Button";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import { AnalyticsCard } from "components/views/Ldb/Dashboard/BusinessAnalytics/Card";
import { formatMoney, getDashboardChartData } from "helpers";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { dashboardActions } from "redux/Ldb/actions";
import useGetBundleDashboardGWM from "hooks/Dashboard/useGetBundleDashboardGWM";
import useGetBundleDashboardGWMTooltip from "hooks/Dashboard/useGetBundleDashboardGWMTooltip";
import Skeleton from "react-loading-skeleton";
import { getSalesChannelIcon } from "utils/get-sales-channel-icon";

type BundledForecastGraphProps = {
    activeForecastViewType?: number;
    activeSkuType?: number;
    salesChannel?: { [key: string]: any }[] | string[];
    isConfigureForecast?: boolean;
};

const BundledForecastGraph = (props: BundledForecastGraphProps) => {
    const { activeForecastViewType, activeSkuType, salesChannel, isConfigureForecast } = props;

    const [activeTab, setActiveTab] = useState<number>(0);
    const [activePeriod, setActivePeriod] = useState<number>(0);
    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [customFilter, setCustomFilter] = useState<string>("");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [chartFilter, setChartFilter] = useState<string>("yearly");
    const [customValue, customOnChange] = useState("");
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [categoryFilter, setCategoryFilter] = useState<string>("");
    const [productFilter, setProductFilter] = useState<string[]>([]);
    const [channelFilter, setChannelFilter] = useState<string>("Shopify");
    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);
    const [cacheData, setCacheData] = useState({});
    const [currYAxisLabel, setCurrYAxisLabel] = useState("");
    const [refresh, setRefresh] = useState<boolean>(false);

    const clearSearchRef = useRef(null);
    const customRef = useRef(null);

    const dateValues = [
        {
            period: "This year",
            value: "yearly",
        },
        {
            period: "This month",
            value: "monthly",
        },
        {
            period: "custom",
            value: "custom",
        },
    ];

    const dispatch = useAppDispatch();
    const { addingForecastConfig, addForecastConfigSuccess } = useAppSelector(
        (state) => state.dashboard
    );

    const {
        data: gwmData,
        isFetching: gwmLoading,
        handleDateRangeChange,
        handleChannelChange,
        handleCategoryChange,
        handleProductChange,
        productIds,
        handleStartAndEndDateChange,
    } = useGetBundleDashboardGWM(true, refresh, setRefresh);

    const {
        data: gwmTooltip,

        handleStartAndEndDateChange: handleTooltipStartAndEndDateChange,
    } = useGetBundleDashboardGWMTooltip();

    const {
        data: productData,
        productSearchList,
        handleDebouncedChangeTwo,
        handleSearchTwo,
        searchTwo,
        isLoadingMore,
    } = useGetProductList(null, "", false, false, "", false);

    const salesChannelOption = (salesChannel as string[])
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon: getSalesChannelIcon(channel) || null,
            };
        });

    const option = {
        grid: { top: 8, right: 0, bottom: 24, left: 0, containLabel: true },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow", // Crosshairs for bars
            },
            enterable: true,
            confine: true,
            formatter: function (params) {
                // Build the tooltip content dynamically

                const data = cacheData?.[chartFilter]?.[params?.[0]?.name] || gwmTooltip;
                const actualSalesTotalQty = data?.actualSales?.reduce(
                    (acc, curr) => acc + curr?.totalQty,
                    0
                );
                const forecastedSalesTotalQty = data?.forecastedSales?.reduce(
                    (acc, curr) => acc + curr?.totalQty,
                    0
                );

                return `
                    <div class=" rounded-lg w-[750px] max-h-[450px]">
                        <div class="font-rocGroteskMedium ">
                            <p class="text-base font-rocGroteskMedium mb-5 pb-1 border-b border-slate-100">
                                Sales and forecast metrics (${params?.[0]?.name})
                            </p>
                           
                            <div class="flex justify-between items-start">
                                <div class="w-1/2 text-slate-800 pr-[28px] border-r border-slate-200">
                                    <div class="flex justify-between items-center pb-1.5 border-b border-slate-100">
                                        <p class="text-[13px] ">Actual sales</p>
                                        <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                            <span>$${formatMoney().format(
                                                data?.totalActualSales
                                            )}</span>
                                            <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                actualSalesTotalQty
                                            )} units</span>
                                        </p>
                                    </div>
                                    <div class="mt-[14px] space-y-[14px] max-h-[250px] overflow-y-auto">
                                        ${data?.actualSales
                                            ?.map((item) => {
                                                return `
                                                    <div>
                                                        <div class="flex justify-between items-center mb-1">
                                                            <p class="text-[13px] ">${truncate(
                                                                item?.inventory?.sku,
                                                                { length: 25 }
                                                            )}</p>
                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                <span>$${formatMoney().format(
                                                                    item?.totalSales
                                                                )}</span>
                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                    item?.totalQty
                                                                )} units</span>
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <details class="bg-slate-50 border-[0.7px] border-b-25 p-1 rounded">
                                                                <summary class="text-[10px] text-b-50 flex items-center justify-between">
                                                                    <span>View product breakdown</span>
                                                                    <i class="ri-arrow-down-s-line text-[14px]"></i>
                                                                </summary>

                                                                <div class="mt-3 space-y-2">
                                                                
                                                                    ${item?.inventory?.bundleItems
                                                                        ?.map((product) => {
                                                                            return `
                                                                        <div class="flex justify-between space-x-1.5 items-center mb-1">
                                                                            <div>
                                                                                <p class="text-xs text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.productName,
                                                                                    { length: 20 }
                                                                                )}</p>
                                                                                <p class="text-[10px] text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.sku,
                                                                                    { length: 30 }
                                                                                )}</p>
                                                                            </div>
                                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                                <span>$${
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.salesPrice
                                                                                        ?.amount
                                                                                        ? formatMoney().format(
                                                                                              product
                                                                                                  ?.inventory
                                                                                                  ?.salesPrice
                                                                                                  ?.amount *
                                                                                                  product?.quantity *
                                                                                                  item?.totalQty
                                                                                          )
                                                                                        : "N/A"
                                                                                }</span>
                                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                                    product?.quantity *
                                                                                        item?.totalQty
                                                                                )} units</span>
                                                                            </p>
                                                                        </div>
                                                                    `;
                                                                        })
                                                                        ?.join("")}
                                                                </div>
                                                            </details>
                                                        </div>
                                                    </div>
                                            `;
                                            })
                                            ?.join("")}
                                        
                                    </div>
                                    
                                </div>

                                <div class="w-1/2 text-slate-800 pl-[28px] ">
                                    <div class="flex justify-between items-center pb-1.5 border-b border-slate-100">
                                        <p class="text-[13px] ">Forecasted sales</p>
                                        <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                            <span>$${formatMoney().format(
                                                data?.totalForecastedSales
                                            )}</span>
                                            <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                forecastedSalesTotalQty
                                            )} units</span>
                                        </p>
                                    </div>
                                    <div class="mt-[14px] space-y-[14px] max-h-[250px] overflow-y-auto">
                                        ${data?.forecastedSales
                                            ?.map((item) => {
                                                return `
                                                    <div>
                                                        <div class="flex justify-between items-center mb-1">
                                                            <p class="text-[13px] ">${truncate(
                                                                item?.inventory?.sku,
                                                                { length: 25 }
                                                            )}</p>
                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                <span>$${formatMoney().format(
                                                                    item?.totalSales
                                                                )}</span>
                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                    item?.totalQty
                                                                )} units</span>
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <details class="bg-slate-50 border-[0.7px] border-b-25 p-1 rounded">
                                                                <summary class="text-[10px] text-b-50 flex items-center justify-between">
                                                                    <span>View product breakdown</span>
                                                                    <i class="ri-arrow-down-s-line text-[14px]"></i>
                                                                </summary>

                                                                <div class="mt-3 space-y-2">
                                                                ${item?.inventory?.bundleItems
                                                                    ?.map((product) => {
                                                                        return `
                                                                        <div class="flex justify-between space-x-1.5 items-center mb-1">
                                                                            <div>
                                                                                <p class="text-xs text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.productName,
                                                                                    { length: 20 }
                                                                                )}</p>
                                                                                <p class="text-[10px] text-slate-500">${truncate(
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.sku,
                                                                                    { length: 30 }
                                                                                )}</p>
                                                                            </div>
                                                                            
                                                                            
                                                                            <p class="font-bold text-[13px] flex items-center space-x-1.5">
                                                                                <span>$${
                                                                                    product
                                                                                        ?.inventory
                                                                                        ?.salesPrice
                                                                                        ?.amount
                                                                                        ? formatMoney().format(
                                                                                              product
                                                                                                  ?.inventory
                                                                                                  ?.salesPrice
                                                                                                  ?.amount *
                                                                                                  product?.quantity *
                                                                                                  item?.totalQty
                                                                                          )
                                                                                        : "N/A"
                                                                                }</span>
                                                                                <span class="text-[10px] text-g-60 px-1.5 h-[18px] rounded border border-g-25 flex items-center justify-center">${formatMoney().format(
                                                                                    product?.quantity *
                                                                                        item?.totalQty
                                                                                )} units</span>
                                                                            </p>
                                                                        </div>
                                                                    `;
                                                                    })
                                                                    ?.join("")}
                                                                    
                                                                </div>
                                                            </details>
                                                        </div>
                                                    </div>
                                            `;
                                            })
                                            ?.join("")}
                                        
                                    </div>
                                    
                                </div>
                            </div>
                                
                        </div>
                    </div>
                `;
            },
            extraCssText: "pointer-events: auto;", // Prevent tooltip from disappearing on hover
        },
        animation: false,
        xAxis: {
            type: "category",
            data: chartData?.[0],
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: "${value}",
            },
        },
        series: [
            {
                name: "Forecast",
                type: "bar",
                data: chartData?.[2]?.map((item) => item?.value),
                barWidth: "55%",
                itemStyle: {
                    color: "#CCCBF6", // Light blue for the forecast bars

                    borderRadius: [10, 10, 0, 0],
                },
                barGap: "-80%", // Overlapping bars
            },
            {
                name: "Actual",
                type: "bar",
                data: chartData?.[1]?.map((item) => item?.value),
                barWidth: "55%",
                itemStyle: {
                    color: "#22214C", // Dark blue for the actual bars
                    borderRadius: [10, 10, 0, 0],
                },
            },
        ],
    };

    const onEvents = {
        mouseover: (params) => {
            if (params.componentType === "series" && params.seriesType === "bar") {
                // Perform any custom action here
                const startOfMonth = moment(params?.name).startOf("month"); // Start of the current week
                const endOfMonth = moment(params?.name).endOf("month"); // End of the current week

                const startOfCurrMonth = moment().startOf("month");

                const startOfWeek = startOfCurrMonth
                    .clone()
                    .add(params?.dataIndex, "weeks")
                    .startOf("week");
                const endOfWeek = startOfWeek.clone().endOf("week");

                if (!cacheData?.[chartFilter]?.[params?.name]) {
                    setCurrYAxisLabel(params?.name);

                    handleTooltipStartAndEndDateChange(
                        chartFilter === "monthly"
                            ? [startOfWeek?.toDate(), endOfWeek?.toDate()]
                            : [startOfMonth?.toDate(), endOfMonth?.toDate()]
                    );
                }
            }
        },
    };

    const handleDateChange = (e) => {
        const { value } = e.target;
        const [val, period] = value.split("-");

        setDurationFilter(period);
        setCustomFilter(val);

        if (val !== "custom") {
            handleDateRangeChange(val);
            setStartAndEndDate([]);
            setChartFilter(val);
            customOnChange("");
        } else {
            setShowCustomDate(true);
        }
    };

    const handleProductValue = (e) => {
        const { value } = e.target;

        const prodIdsCopy = [...productIds];
        const productFilterCopy = [...productFilter];
        const newValue = value?.split("-")[0];
        const newName = value?.split("-")[1];
        const isValuePresent = prodIdsCopy?.find((id) => id === newValue);

        if (isValuePresent) {
            handleProductChange(prodIdsCopy?.filter((id) => id !== newValue));
            setProductFilter(productFilterCopy?.filter((name) => name !== newName));
        } else {
            if (newValue === "") {
                handleProductChange([]);
                setProductFilter([]);
            } else {
                setProductFilter((prev) => [...prev, newName]);
                handleProductChange([...prodIdsCopy, newValue]);
            }
        }

        handleCategoryChange("");
        setCategoryFilter("");
    };

    const handleChangeValue = (e) => {
        const { value } = e.target;

        setChannelFilter(value);

        if (value === "All channels") {
            handleChannelChange("");
        } else {
            handleChannelChange(value);
        }
    };

    useEffect(() => {
        if (gwmTooltip && chartFilter && currYAxisLabel !== "") {
            setCacheData((prev) => ({
                ...prev,
                [chartFilter]: {
                    ...prev?.[chartFilter],
                    [currYAxisLabel]: gwmTooltip,
                },
            }));
        }
    }, [chartFilter, gwmTooltip, currYAxisLabel]);

    useEffect(() => {
        if (gwmData) {
            let chartDataValues;
            if (customFilter === "custom" && startAndEndDate?.length === 2) {
                chartDataValues = getDashboardChartData(gwmData, "custom", startAndEndDate);
            } else {
                chartDataValues = getDashboardChartData(gwmData, chartFilter);
            }
            setChartData(chartDataValues);
        }
    }, [chartFilter, gwmData, startAndEndDate]);

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    useEffect(() => {
        if (durationFilter === "custom" && customValue[0] !== customValue[1]) {
            const dates = [
                moment(customValue?.[0]).format("MMM DD YYYY"),
                moment(customValue?.[1]).format("MMM DD YYYY"),
            ];

            setStartAndEndDate([dates?.[0], dates?.[1]]);
            if (customValue?.[0] !== customValue?.[1]) {
                handleStartAndEndDateChange(customValue);
                handleTooltipStartAndEndDateChange(customValue);
            }
        }
    }, [durationFilter, customValue]);

    return (
        <div>
            {activeForecastViewType === 0 && activeSkuType === 1 && !isConfigureForecast && (
                <div className=" absolute top-[80px] right-[32px]">
                    <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                        <Button
                            btnText={"Refresh"}
                            btnClassname={
                                "!w-fit !rounded-[6px]  !bg-[#E6F7E6] !text-[#0AAD0A] !shadow-buttonShadow2 !border !border-slate-100 !bg-[#FBFDFF] !py-[9px] !text-g-55"
                            }
                            onClick={() => {
                                setRefresh(true);
                            }}
                            icon={<i className="ri-refresh-line text-g-55"></i>}
                        />

                        <div className="sm:hidden w-[1px] h-[20px] xs:h-[25px] bg-slate-200"></div>

                        <Dropdown
                            handleChange={(name, value) => {
                                handleProductValue({
                                    target: { name, value },
                                });
                            }}
                            dropdown={
                                <div className="flex items-center sm:rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 sm:!border sm:!border-slate-100">
                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block`}
                                    >
                                        {productFilter?.length > 0
                                            ? truncate(productFilter?.join(","), {
                                                  length: 15,
                                              })
                                            : "All products"}
                                    </div>
                                    <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                </div>
                            }
                            dropdownItemsClasses="!px-0 !py-0"
                            dropdownClassName="!w-full !mb-10 !-mt-7"
                            dropdownContainerClasses={`sm:shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] 
                                            !min-h-[300px] !max-h-[300px] !w-auto !pt-0
                                    `}
                            loader={isLoadingMore}
                            name="valueRange"
                            search={
                                <div ref={clearSearchRef} className="">
                                    <TextInput
                                        value={searchTwo}
                                        name={"search"}
                                        type={"text"}
                                        required={false}
                                        onChange={handleSearchTwo}
                                        onInput={handleDebouncedChangeTwo}
                                        inputPlaceholder="Search product"
                                        containerClassname=""
                                        inputContainerClassname={"!h-[40px]"}
                                    />
                                </div>
                            }
                            leaveOpen={true}
                            dropdownOptions={[
                                {
                                    label: (
                                        <div
                                            className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                        >
                                            <i
                                                className={`${
                                                    productIds?.length === 0
                                                        ? "ri-checkbox-line text-carribean-green"
                                                        : "ri-checkbox-blank-line text-slate-200"
                                                }    text-xl`}
                                            ></i>
                                            All products
                                        </div>
                                    ),
                                    value: "",
                                },
                                ...productSearchList?.map((item, idx) => ({
                                    label: (
                                        <div
                                            key={idx}
                                            className=" text-slate-600 font-rocGroteskMedium text-[13px] gap-2 flex items-center
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                        >
                                            <i
                                                className={`${
                                                    productIds?.includes(item?._id)
                                                        ? "ri-checkbox-line text-carribean-green"
                                                        : "ri-checkbox-blank-line text-slate-200"
                                                }   text-xl`}
                                            ></i>

                                            <div className="flex flex-col">
                                                <p className="text-xs mb-1">{` SKU: ${item?.sku}`}</p>
                                                <span className="text-[10px] leading-normal">
                                                    {truncate(item?.productName, {
                                                        length: 50,
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                    ),
                                    value: item?._id + "-" + item?.productName,
                                })),
                            ]}
                        />
                        <div className="sm:hidden w-[1px] h-[20px] xs:h-[25px] bg-slate-200"></div>

                        <Dropdown
                            handleChange={(name, value) => {
                                handleChangeValue({
                                    target: { name, value },
                                });
                            }}
                            dropdown={
                                <div className="flex items-center justify-between rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block  ${
                                            channelFilter && "capitalize"
                                        }`}
                                    >
                                        {truncate(channelFilter, {
                                            length: 15,
                                        }) || "All channels"}
                                    </div>
                                    <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                </div>
                            }
                            dropdownItemsClasses=""
                            dropdownClassName="!mb-10 !-mt-7"
                            dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min
                                    !py-0`}
                            name="valueRange"
                            dropdownOptions={[
                                {
                                    label: "All channels",
                                    value: "",
                                },
                                ...salesChannelOption?.map((item, idx) => ({
                                    label: item.label,
                                    value: item.value,
                                    icon: item?.icon || null,
                                })),
                            ]}
                        />
                        <div className="">
                            <Dropdown
                                handleChange={(name, value) => {
                                    setActivePeriod(0);
                                    handleDateChange({ target: { name, value } });
                                }}
                                dropdown={
                                    <div className="flex items-center justify-between rounded-[6px] shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100 max-sm:hidden">
                                        <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>

                                        <div
                                            className={`text-g-75 font-rocGroteskMedium text-[13px] inline-block ${
                                                durationFilter === "custom" && "capitalize"
                                            } `}
                                        >
                                            {startAndEndDate?.length !== 0
                                                ? ` ${startAndEndDate[0]} to ${startAndEndDate[1]}`
                                                : durationFilter || "This year"}
                                        </div>
                                    </div>
                                }
                                dropdownItemsClasses="!px-0 !py-0"
                                dropdownClassName="!w-full !mb-10 !-mt-7"
                                dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                                name="valueRange"
                                dropdownOptions={[
                                    ...dateValues
                                        ?.filter((item) =>
                                            activeTab === 0 ? true : item?.value !== "custom"
                                        )
                                        ?.map((item, idx) => ({
                                            label: (
                                                <div
                                                    key={idx}
                                                    className={`${
                                                        item.period === "custom" && "capitalize"
                                                    } text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                        border-b border-b-slate-100 !w-[120px] px-4 py-[10px]`}
                                                >
                                                    {item?.period}
                                                </div>
                                            ),
                                            value: item?.value + "-" + item?.period,
                                        })),
                                ]}
                            />
                        </div>
                        <div className="relative">
                            {showCustomDate && (
                                <div
                                    ref={customRef}
                                    className="w-[630px]  z-[10000] absolute  right-0 top-16 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                                >
                                    <CustomCalendar
                                        showDoubleView={true}
                                        selectRange={true}
                                        value={customValue}
                                        onChange={customOnChange}
                                    />
                                    <div className="flex justify-end">
                                        <Button
                                            btnText={"Close"}
                                            btnClassname={"!w-fit !py-2"}
                                            onClick={() => setShowCustomDate(false)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div>
                <div className="lg:flex justify-between items-start mt-8">
                    <div className="flex flex-col gap-3.5 mb-6 lg:mb-0">
                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center space-x-1">
                                <span className="w-[14px] h-[14px] rounded-sm bg-[#22214C]" />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Actual bundle sales
                                </p>
                            </div>
                        )}

                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center space-x-1">
                                <span className="w-[14px] h-[14px] rounded-sm bg-[#CCCBF6]" />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Bundle demand forecast
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex max-lg:flex-col">
                        <div className="flex w-full lg:w-fit max-lg:items-center max-lg:justify-between max-lg:border-b max-lg:border-b-slate-200">
                            {gwmLoading ? (
                                <div className="flex flex-col gap-2 items-end pr-10">
                                    <Skeleton
                                        height={10}
                                        width={90}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={40}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={200}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                </div>
                            ) : (
                                <AnalyticsCard
                                    title={"Forecasted revenue"}
                                    figure={
                                        gwmData?.forecastTotalSale !== undefined
                                            ? millify(Number(gwmData?.forecastTotalSale), {
                                                  precision: 2,
                                              })
                                            : "----"
                                    }
                                    currency={"$"}
                                    customFigure={
                                        Boolean(gwmData?.configuredRevForecast) &&
                                        gwmData?.configuredRevForecast !== undefined
                                            ? millify(Number(gwmData?.configuredRevForecast), {
                                                  precision: 2,
                                              })
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hideDirection={true}
                                    hasCustomValue={true}
                                    increaseClassName="!text-g-60"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's revenue forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured revenue forecast"}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none flex-1 lg:!w-auto"
                                    arrUpClass={"!text-g-60"}
                                    titleClassName="!text-slate-600 !text-[10px]"
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[250px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent before:!border-r-gm-75 before:!top-[40%] before:!-left-[9%] !z-[999] before:!border-[6px] !left-[50px] xs:!left-[120px]"
                                    toolTipContent={
                                        <div className="space-y-2 p-3">
                                            <div className="">
                                                <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                    Synth's revenue forecast
                                                </p>
                                                <p className="text-[11px]">
                                                    This is the total amount our system has
                                                    forecasted you would sell for all your bundles
                                                    over the next 6 months. We use the current unit
                                                    bundle value/amount multiplied by the forecasted
                                                    quantity.
                                                </p>
                                            </div>

                                            <div className="">
                                                <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                    Configured revenue forecast
                                                </p>
                                                <p className="text-[11px]">
                                                    his is the total forecasted revenue for all your
                                                    bundles that you have configured over the next 6
                                                    months. We use the current unit bundle
                                                    value/amount multiplied by the forecasted
                                                    quantity you set.
                                                </p>
                                            </div>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total amount our system has forecasted you would sell for all your bundles over the next 6 months. We use the current unit bundle value/amount multiplied by the forecasted quantity."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total forecasted revenue for all your bundles that you have configured over the next 6 months. We use the current unit bundle value/amount multiplied by the forecasted quantity you set."
                                    }
                                />
                            )}

                            <div className="h-[29px] lg:hidden w-[1px] bg-slate-200"></div>

                            {gwmLoading ? (
                                <div className="flex flex-col gap-2 items-end pr-10">
                                    <Skeleton
                                        height={10}
                                        width={90}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={40}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={200}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                </div>
                            ) : (
                                <AnalyticsCard
                                    title={"Forecasted sales quantity"}
                                    value={Math.round(gwmData?.totalForecastQty)}
                                    customFigure={
                                        gwmData?.configuredDemandForecast
                                            ? formatMoney().format(
                                                  gwmData?.configuredDemandForecast
                                              )
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hasCustomValue={true}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-[15px] !pr-8  !rounded-none  flex-1 lg:!w-fit"
                                    titleClassName="!text-slate-500 !text-[10px]"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's demand forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured demand forecast"}
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-r-transparent lg:before:!border-l-transparent before:!border-l-gm-75 lg:before:!border-r-gm-75 before:!top-[40%] before:!-right-[95px] lg:before:!right-auto lg:before:!-left-[9%] !z-[999] before:!border-[6px] !-left-[100px] lg:!left-[120px]"
                                    toolTipContent={
                                        <div className="!p-3">
                                            <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                Forecasted sales quantity
                                            </p>
                                            <p className="text-[11px]">
                                                This is the estimated number of units of all
                                                products that you are likely to sell in a future
                                                period.
                                            </p>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total number of units across all your bundles that you are most likely going to sell over the next 6 months."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total quantity across all your bundles that you have setup as your likely forecasted units to be sold."
                                    }
                                />
                            )}
                        </div>

                        {gwmLoading ? (
                            <div className="flex flex-col gap-2 items-end pr-10">
                                <Skeleton
                                    height={10}
                                    width={90}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                                <Skeleton
                                    height={10}
                                    width={40}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                                <Skeleton
                                    height={10}
                                    width={200}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                            </div>
                        ) : (
                            <AnalyticsCard
                                title={"Forecasted inventory deficit"}
                                value={Math.round(gwmData?.forecastedStockNeeded)}
                                loader={false}
                                iconTwo
                                addTextTwo={"(oos)"}
                                addTextTwoClass="!text-r-55 !text-xl"
                                textAndSubtextContainer={"!justify-end"}
                                className="!shadow-none !border-none !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none !w-auto"
                                titleClassName="!text-slate-600 !text-[10px]"
                                figureClassName="!mt-2.5 !text-r-55  !text-[20px] !leading-[28px] flex gap-1 items-end"
                                tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent lg:before:!border-r-transparent before:!border-r-gm-75 lg:before:!border-l-gm-75 before:!top-[40%] before:!-left-[9%] lg:before:!left-auto lg:before:!-right-[6px] !z-[999] before:!border-[6px] !left-[0px] xs:!left-[120px] lg:!-left-[250px]"
                                toolTipContent={
                                    <div className="!p-3">
                                        <p className="underline text-xs font-rocGroteskMedium mb-1">
                                            Forecasted inventory deficit
                                        </p>
                                        <p className="text-[11px] ">
                                            This is the estimated number of units of all your
                                            products that are likely to be out of stock in a future
                                            period. These are the likely out of stock units of
                                            products with respect to the demand forecast.
                                        </p>
                                    </div>
                                }
                                addTextTwoTooltipContent={
                                    "This is the total number of units you would need to meet up the forecasted sales quantity over the next 6 months, based on your current inventory level today."
                                }
                            />
                        )}
                    </div>
                </div>
                {productData?.length !== 0 &&
                !gwmLoading &&
                gwmData?.currentSales?.length === 0 &&
                gwmData?.forcastSales?.length === 0 ? (
                    <div className="w-[100%] h-[400px] flex items-center justify-center">
                        <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                            <img
                                src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1717426949/icon_w8c4mn.png"
                                alt=""
                                className=""
                            />
                            <p className="text-g-75 font-rocGroteskBold text-base ">
                                {" "}
                                No forecast data to show for this query{" "}
                            </p>
                        </div>
                    </div>
                ) : gwmLoading ? (
                    <div className="mt-20">
                        <div className="flex gap-5">
                            <div className="flex flex-col justify-between gap-[17px]">
                                <span className="block text-[9px] text-slate-500">70k</span>
                                <span className="block text-[9px] text-slate-500">60k</span>
                                <span className="block text-[9px] text-slate-500">50k</span>
                                <span className="block text-[9px] text-slate-500">40k</span>
                                <span className="block text-[9px] text-slate-500">30k</span>
                                <span className="block text-[9px] text-slate-500">20k</span>
                                <span className="block text-[9px] text-slate-500">10k</span>
                            </div>

                            <div className="flex gap-2 justify-between w-full">
                                {Array.from({ length: 6 }).map((_, idx) => (
                                    <Skeleton
                                        key={idx}
                                        height={300}
                                        width={50}
                                        highlightColor="#E2E8F0"
                                        borderRadius={6}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                            <span className="block text-[9px] text-slate-500">Jan</span>
                            <span className="block text-[9px] text-slate-500">Feb</span>
                            <span className="block text-[9px] text-slate-500">Mar</span>
                            <span className="block text-[9px] text-slate-500">Apr</span>
                            <span className="block text-[9px] text-slate-500">May</span>
                            <span className="block text-[9px] text-slate-500">Jun</span>
                        </div>
                    </div>
                ) : (
                    <div className="mt-10 mb-6 w-full">
                        <ReactEcharts
                            option={option}
                            onEvents={onEvents}
                            style={{ height: "450px", width: "100%" }}
                        />
                    </div>
                )}
            </div>

            {gwmLoading && (
                <div className="w-full h-full absolute top-0 left-0">
                    <div className="w-full h-full flex justify-center items-center">
                        {/* <Loader size={10} /> */}
                        <p className="text-slate-600 text-base font-rocGroteskMedium">
                            Generating forecast...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BundledForecastGraph;
